import { createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "bookings-table card border" }
const _hoisted_2 = { class: "px-2 pt-1 pb-1" }
const _hoisted_3 = { class: "form-check mt-1" }
const _hoisted_4 = {
  key: 0,
  class: "table-container"
}
const _hoisted_5 = { class: "table-responsive" }
const _hoisted_6 = { class: "table table-striped table-hover" }
const _hoisted_7 = { class: "table-light" }
const _hoisted_8 = { class: "alert alert-danger m-3" }
const _hoisted_9 = { class: "table-container" }
const _hoisted_10 = { class: "table-controls mb-3 px-3" }
const _hoisted_11 = { class: "row align-items-center" }
const _hoisted_12 = { class: "col-md-6" }
const _hoisted_13 = { class: "d-flex align-items-center gap-2" }
const _hoisted_14 = ["value"]
const _hoisted_15 = { class: "col-md-6 text-md-end" }
const _hoisted_16 = { class: "text-muted" }
const _hoisted_17 = {
  class: "table-responsive",
  style: {"overflow-x":"auto"}
}
const _hoisted_18 = { class: "table-light" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "d-flex align-items-center justify-content-between" }
const _hoisted_21 = {
  key: 0,
  class: "sort-indicator"
}
const _hoisted_22 = {
  key: 0,
  class: "fas fa-sort-up"
}
const _hoisted_23 = {
  key: 1,
  class: "fas fa-sort-down"
}
const _hoisted_24 = {
  key: 2,
  class: "fas fa-sort text-muted"
}
const _hoisted_25 = {
  key: 0,
  "aria-label": "Pagination",
  class: "px-3 pb-3"
}
const _hoisted_26 = { class: "d-flex justify-content-between align-items-center" }
const _hoisted_27 = ["disabled"]
const _hoisted_28 = ["disabled"]
const _hoisted_29 = { class: "d-flex align-items-center gap-2" }
const _hoisted_30 = ["value", "max"]
const _hoisted_31 = { class: "text-muted" }

import { computed, ref, watch } from "vue";
import { FlexRender } from "@tanstack/vue-table";
import { useQuery } from "@tanstack/vue-query";
import { fetchMyBookings, queryKeys } from "../../api/index.js";
import { useBookingsTable } from "../composables/useBookingsTable.js";
import { useBookingActions } from "../../composables/useBookingActions.js";
import { useMasterDataHelpers } from "../../calendar/composables/useMasterDataHelpers.js";
import BookingDateFilters from "./BookingDateFilters.vue";
import dayjs from "dayjs";
import "dayjs/locale/de";

// Configure dayjs to use German locale

export default {
  __name: 'BookingsTable',
  props: {
	dateRange: {
		type: Object,
		default: () => ({}),
	},
},
  setup(__props) {

dayjs.locale("de");

const props = __props;

// Internal date range state - default to today if no props provided
const getTodayRange = () => {
	const today = dayjs().format('YYYY-MM-DD');
	return { from: today, until: today };
};

const internalDateRange = ref({
	from: props.dateRange.from || getTodayRange().from,
	until: props.dateRange.until || getTodayRange().until,
});

// Watch for prop changes and update internal state
watch(
	() => props.dateRange,
	(newDateRange) => {
		internalDateRange.value = { ...newDateRange };
	},
	{ immediate: true }
);

// Fetch bookings using TanStack Query with reactive date range
// We need to use useQuery directly with computed parameters to ensure reactivity works
const {
	data: queryData,
	isLoading: loading,
	error,
	refetch,
	isFetching: isRefetching,
} = useQuery({
	queryKey: computed(() =>
		queryKeys.myBookings({
			from: internalDateRange.value.from,
			until: internalDateRange.value.until,
		})
	),
	queryFn: ({ signal }) => {
		if (signal?.aborted) {
			throw new Error("Query was cancelled");
		}
		return fetchMyBookings({
			from: internalDateRange.value.from,
			until: internalDateRange.value.until,
		});
	},
	staleTime: 1000 * 60 * 2, // 2 minutes - booking data changes frequently
	gcTime: 1000 * 60 * 10, // 10 minutes
	retry: 2,
	retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
	refetchOnWindowFocus: true, // Refetch when user returns to tab
});

// Extract bookings from query data
const bookings = computed(() => {
	// Safely handle the query data
	if (!queryData.value) {
		return [];
	}
	return queryData.value.bookings || [];
});

// Whether cancelled (deleted) bookings should be shown - hidden by default
const showDeletedBookings = ref(false);

// Bookings shown in the table, optionally excluding cancelled ones
const filteredBookings = computed(() => {
	if (showDeletedBookings.value) {
		return bookings.value;
	}
	return bookings.value.filter((booking) => !booking.cancelledAt);
});

// Get booking actions from provider
const { editBooking, deleteBooking } = useBookingActions();

// Get master data helpers for room lookups and user role
const masterData = useMasterDataHelpers();

// Create reactive computed for showing points column
const showPointsColumn = computed(() => masterData.userRole.value === 'student');

// The booking type (Punktebuchung / Buchung ohne Punkte / Sofortbuchung) is only a
// meaningful distinction for students - staff bookings never consume points.
const showTypeColumn = computed(() => masterData.userRole.value === 'student');

// Setup table with bookings data and pass the actions directly
const { table } = useBookingsTable(() => filteredBookings.value, {
	onEditBooking: (booking) => editBooking(booking),
	onCancelBooking: (booking) => deleteBooking(booking),
}, {
	showPointsColumn,
	showTypeColumn,
}, masterData);

// Helper function for skeleton cell widths - matching new column order and sizes
const getSkeletonWidth = (col) => {
	// Dynamic column order based on user role
	const baseWidths = ["180px"]; // Termin
	const pointsWidth = ["80px"]; // Punkte (only for students)
	const typeWidth = ["140px"]; // Art (only for students)
	const otherWidths = ["85px", "150px", "90px", "80px"]; // Raum, Titel, Dauer, Aktionen

	const widths = [
		...baseWidths,
		...(showPointsColumn.value ? pointsWidth : []),
		...(showTypeColumn.value ? typeWidth : []),
		...otherWidths,
	];

	return widths[col - 1] || "80px";
};

// Computed property for total column count
const totalColumns = computed(() => {
	// Termin, Raum, Titel, Dauer, Aktionen plus the student-only Punkte and Art columns
	return 5 + (showPointsColumn.value ? 1 : 0) + (showTypeColumn.value ? 1 : 0);
});


// Helper functions for booking status
const isBookingToday = (booking) => {
	if (!booking.startTime) return false;
	return dayjs(booking.startTime).isSame(dayjs(), "day");
};

const isBookingOngoing = (booking) => {
	if (!booking.startTime || !booking.endTime) return false;
	const now = dayjs();
	const start = dayjs(booking.startTime);
	const end = dayjs(booking.endTime);
	return now.isAfter(start) && now.isBefore(end);
};

// Get row classes based on booking status
const getRowClasses = (booking) => {
	const classes = [];
	if (isBookingToday(booking)) {
		classes.push("booking-today");
	}
	if (isBookingOngoing(booking)) {
		classes.push("booking-ongoing");
	}
	return classes.join(" ");
};

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[16] || (_cache[16] = _createElementVNode("div", { class: "card-header bg-white border-0 p-2" }, [
      _createElementVNode("div", { class: "d-flex justify-content-between align-items-center" }, [
        _createElementVNode("h6", { class: "mb-0 fw-bold" }, " Meine Buchungen ")
      ])
    ], -1 /* CACHED */)),
    _createCommentVNode(" Date Filters "),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(BookingDateFilters, {
        "date-range": internalDateRange.value,
        "onUpdate:dateRange": _cache[0] || (_cache[0] = $event => ((internalDateRange).value = $event))
      }, null, 8 /* PROPS */, ["date-range"]),
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("input", {
          id: "showDeletedBookings",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((showDeletedBookings).value = $event)),
          class: "form-check-input",
          type: "checkbox"
        }, null, 512 /* NEED_PATCH */), [
          [_vModelCheckbox, showDeletedBookings.value]
        ]),
        _cache[6] || (_cache[6] = _createElementVNode("label", {
          class: "form-check-label",
          for: "showDeletedBookings"
        }, " Stornierte Buchungen anzeigen ", -1 /* CACHED */))
      ])
    ]),
    _createCommentVNode(" Loading State "),
    (_unref(loading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createCommentVNode(" Table Controls Skeleton "),
          _cache[8] || (_cache[8] = _createStaticVNode("<div class=\"table-controls mb-3 p-3\"><div class=\"row align-items-center\"><div class=\"col-md-6\"><div class=\"d-flex align-items-center gap-2\"><div class=\"skeleton skeleton-label\"></div><div class=\"skeleton skeleton-select\"></div></div></div><div class=\"col-md-6 text-md-end\"><div class=\"skeleton skeleton-info\"></div></div></div></div>", 1)),
          _createCommentVNode(" Table Skeleton "),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("table", _hoisted_6, [
              _createElementVNode("thead", _hoisted_7, [
                _createElementVNode("tr", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(totalColumns.value, (n) => {
                    return (_openBlock(), _createElementBlock("th", { key: n }, [...(_cache[7] || (_cache[7] = [
                      _createElementVNode("div", { class: "skeleton skeleton-header" }, null, -1 /* CACHED */)
                    ]))]))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ]),
              _createElementVNode("tbody", null, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                  return _createElementVNode("tr", { key: n }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(totalColumns.value, (col) => {
                      return (_openBlock(), _createElementBlock("td", { key: col }, [
                        _createElementVNode("div", {
                          class: "skeleton skeleton-cell",
                          style: _normalizeStyle({ width: getSkeletonWidth(col) })
                        }, null, 4 /* STYLE */)
                      ]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ])
                }), 64 /* STABLE_FRAGMENT */))
              ])
            ])
          ])
        ]))
      : (_unref(error))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createCommentVNode(" Error State "),
            _createElementVNode("div", _hoisted_8, [
              _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fas fa-exclamation-triangle me-2" }, null, -1 /* CACHED */)),
              _createTextVNode(" " + _toDisplayString(_unref(error)?.message ||
          _unref(error) ||
          "Ein Fehler ist aufgetreten beim Laden der Buchungen."), 1 /* TEXT */)
            ])
          ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
        : (!filteredBookings.value || filteredBookings.value.length === 0)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createCommentVNode(" Empty State "),
              _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-center py-5" }, [
                _createElementVNode("i", {
                  class: "fas fa-calendar text-muted",
                  style: {"font-size":"3rem"}
                }),
                _createElementVNode("h4", { class: "mt-3 text-muted" }, " Keine Buchungen gefunden "),
                _createElementVNode("p", { class: "text-muted" }, " Sie haben derzeit keine Buchungen. ")
              ], -1 /* CACHED */))
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createCommentVNode(" Table "),
              _createElementVNode("div", _hoisted_9, [
                _createCommentVNode(" Table Controls "),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("select", {
                          value: _unref(table).getState().pagination.pageSize,
                          class: "form-select form-select-sm",
                          style: {"width":"auto"},
                          onChange: _cache[2] || (_cache[2] = (e) => _unref(table).setPageSize(Number(e.target.value)))
                        }, [...(_cache[11] || (_cache[11] = [
                          _createElementVNode("option", { value: "5" }, " 5 ", -1 /* CACHED */),
                          _createElementVNode("option", { value: "10" }, " 10 ", -1 /* CACHED */),
                          _createElementVNode("option", { value: "25" }, " 25 ", -1 /* CACHED */),
                          _createElementVNode("option", { value: "50" }, " 50 ", -1 /* CACHED */)
                        ]))], 40 /* PROPS, NEED_HYDRATION */, _hoisted_14),
                        _cache[12] || (_cache[12] = _createElementVNode("label", { class: "form-label mb-0" }, "Einträge pro Seite", -1 /* CACHED */))
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("small", _hoisted_16, " Zeige " + _toDisplayString(_unref(table).getState().pagination.pageIndex *
                  _unref(table).getState().pagination.pageSize +
                  1) + " bis " + _toDisplayString(Math.min(
                  (_unref(table).getState().pagination.pageIndex + 1) *
                    _unref(table).getState().pagination.pageSize,
                  _unref(table).getFilteredRowModel().rows.length
                )) + " von " + _toDisplayString(_unref(table).getFilteredRowModel().rows.length) + " Buchungen ", 1 /* TEXT */)
                    ])
                  ])
                ]),
                _createCommentVNode(" Responsive Table Wrapper "),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("table", {
                    class: "table table-striped table-hover",
                    style: _normalizeStyle({ width: _unref(table).getTotalSize() + 'px' })
                  }, [
                    _createElementVNode("thead", _hoisted_18, [
                      _createElementVNode("tr", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getFlatHeaders(), (header) => {
                          return (_openBlock(), _createElementBlock("th", {
                            key: header.id,
                            class: _normalizeClass({
                  sortable: header.column.getCanSort(),
                }),
                            style: _normalizeStyle({ width: header.getSize() + 'px' }),
                            onClick: $event => (header.column.getToggleSortingHandler()?.($event))
                          }, [
                            _createElementVNode("div", _hoisted_20, [
                              _createVNode(_unref(FlexRender), {
                                render: header.column.columnDef.header,
                                props: header.getContext()
                              }, null, 8 /* PROPS */, ["render", "props"]),
                              (header.column.getCanSort())
                                ? (_openBlock(), _createElementBlock("span", _hoisted_21, [
                                    (header.column.getIsSorted() === 'asc')
                                      ? (_openBlock(), _createElementBlock("i", _hoisted_22))
                                      : (header.column.getIsSorted() === 'desc')
                                        ? (_openBlock(), _createElementBlock("i", _hoisted_23))
                                        : (_openBlock(), _createElementBlock("i", _hoisted_24))
                                  ]))
                                : _createCommentVNode("v-if", true)
                            ])
                          ], 14 /* CLASS, STYLE, PROPS */, _hoisted_19))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getRowModel().rows, (row) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: row.id
                        }, [
                          _createCommentVNode(" Regular data row "),
                          _createElementVNode("tr", {
                            class: _normalizeClass([
                  'main-row',
                  getRowClasses(row.original),
                ])
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                              return (_openBlock(), _createElementBlock("td", {
                                key: cell.id,
                                class: _normalizeClass({ 'deleted-booking': row.original.cancelledAt && cell.column.id !== 'actions' }),
                                style: _normalizeStyle({ width: cell.column.getSize() + 'px' })
                              }, [
                                _createVNode(_unref(FlexRender), {
                                  render: cell.column.columnDef.cell,
                                  props: cell.getContext()
                                }, null, 8 /* PROPS */, ["render", "props"])
                              ], 6 /* CLASS, STYLE */))
                            }), 128 /* KEYED_FRAGMENT */))
                          ], 2 /* CLASS */)
                        ], 64 /* STABLE_FRAGMENT */))
                      }), 128 /* KEYED_FRAGMENT */))
                    ])
                  ], 4 /* STYLE */)
                ]),
                _createCommentVNode(" Pagination "),
                (_unref(table).getPageCount() > 1)
                  ? (_openBlock(), _createElementBlock("nav", _hoisted_25, [
                      _createElementVNode("div", _hoisted_26, [
                        _createElementVNode("div", null, [
                          _createElementVNode("button", {
                            class: "btn btn-outline-primary me-2",
                            disabled: !_unref(table).getCanPreviousPage(),
                            onClick: _cache[3] || (_cache[3] = $event => (_unref(table).previousPage()))
                          }, [...(_cache[13] || (_cache[13] = [
                            _createElementVNode("i", { class: "fas fa-chevron-left me-1" }, null, -1 /* CACHED */),
                            _createTextVNode(" Zurück ", -1 /* CACHED */)
                          ]))], 8 /* PROPS */, _hoisted_27),
                          _createElementVNode("button", {
                            class: "btn btn-outline-primary",
                            disabled: !_unref(table).getCanNextPage(),
                            onClick: _cache[4] || (_cache[4] = $event => (_unref(table).nextPage()))
                          }, [...(_cache[14] || (_cache[14] = [
                            _createTextVNode(" Weiter ", -1 /* CACHED */),
                            _createElementVNode("i", { class: "fas fa-chevron-right ms-1" }, null, -1 /* CACHED */)
                          ]))], 8 /* PROPS */, _hoisted_28)
                        ]),
                        _createElementVNode("div", _hoisted_29, [
                          _cache[15] || (_cache[15] = _createElementVNode("span", { class: "text-muted" }, "Seite", -1 /* CACHED */)),
                          _createElementVNode("input", {
                            value: _unref(table).getState().pagination.pageIndex + 1,
                            type: "number",
                            min: "1",
                            max: _unref(table).getPageCount(),
                            class: "form-control form-control-sm",
                            style: {"width":"80px"},
                            onChange: _cache[5] || (_cache[5] = (e) => _unref(table).setPageIndex(Number(e.target.value) - 1))
                          }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_30),
                          _createElementVNode("span", _hoisted_31, "von " + _toDisplayString(_unref(table).getPageCount()), 1 /* TEXT */)
                        ])
                      ])
                    ]))
                  : _createCommentVNode("v-if", true)
              ])
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
  ]))
}
}

}